import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { meResponse, type MeResponse } from "@pharmacy/contracts";

import { api, type ApiClient, type SessionLostReason, type WebTokens } from "@/api/client";

/**
 * Who is signed in (based on apps/mobile/src/auth/session.tsx).
 *
 * One state, held above the router. Pages read it; nothing else decides
 * whether somebody is signed in, and no page touches a token.
 *
 * Unlike the phone there is no saved identity to open on: the web is online
 * only and keeps nothing about the pharmacy in the browser. Opening the page
 * asks the server (refresh cookie → access token → /auth/me) behind a spinner.
 */

export type SessionState =
  | { status: "restoring" }
  /**
   * `reason`: an error code saying why the server ended the session, for the
   * welcome page. `left`: the person pressed Sign out.
   */
  | { status: "signedOut"; reason?: SessionLostReason; left?: true }
  | { status: "signedIn"; me: MeResponse };

interface SessionValue {
  state: SessionState;
  /** After a sign-in page succeeds, with what the server answered. */
  signIn: (tokens: WebTokens) => Promise<void>;
  signOut: () => Promise<void>;
  /** Read /auth/me again, e.g. after an owner changes what somebody may open. */
  refreshIdentity: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children, client = api }: { children: React.ReactNode; client?: ApiClient }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<SessionState>({ status: "restoring" });

  const loadIdentity = useCallback(() => client.get("/auth/me", { schema: meResponse }), [client]);

  // The server ended the session (signed out elsewhere, pharmacy deleted…).
  useEffect(
    () =>
      client.onSessionLost((reason) => {
        queryClient.clear();
        setState({ status: "signedOut", reason });
      }),
    [client, queryClient],
  );

  // Opening the page. Two calls in development (StrictMode) share one refresh:
  // the client single-flights it, so the cookie is never presented twice.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const restored = await client.restore();
        if (!restored.signedIn) {
          if (!cancelled) setState({ status: "signedOut", reason: restored.reason });
          return;
        }
        const me = await loadIdentity();
        if (!cancelled) setState({ status: "signedIn", me });
      } catch {
        // No internet, or the server is down. Not signed in on this page; the
        // sign-in page then says what is wrong when they try.
        client.forget();
        if (!cancelled) setState({ status: "signedOut" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, loadIdentity]);

  const signIn = useCallback(
    async (tokens: WebTokens) => {
      client.setTokens(tokens);
      // Nothing from a previous person may survive into this one's pages.
      queryClient.clear();
      const me = await loadIdentity();
      setState({ status: "signedIn", me });
    },
    [client, loadIdentity, queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await client.logout();
    } catch {
      // The page forgets the session anyway, and the server's ends on its own.
    }
    queryClient.clear();
    setState({ status: "signedOut", left: true });
  }, [client, queryClient]);

  const refreshIdentity = useCallback(async () => {
    const me = await loadIdentity();
    setState((prev) => (prev.status === "signedIn" ? { status: "signedIn", me } : prev));
  }, [loadIdentity]);

  const value = useMemo(() => ({ state, signIn, signOut, refreshIdentity }), [state, signIn, signOut, refreshIdentity]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside a SessionProvider");
  return value;
}

/**
 * The signed-in person, for pages behind the sign-in gate. Throws rather than
 * returning null so a page never handles a case the router already prevents.
 */
export function useMe(): MeResponse {
  const { state } = useSession();
  if (state.status !== "signedIn") throw new Error("useMe called outside a signed-in page");
  return state.me;
}
